import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password reset successfully');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="reset-password-page">
        <div className="w-full max-w-md bg-white border-2 border-red-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="font-bold text-slate-800 text-lg mb-2">Invalid reset link</h2>
          <p className="text-slate-600 text-sm mb-5">
            The password reset link is missing a token. Please request a new one.
          </p>
          <Link to="/forgot-password">
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 font-semibold">
              Request new link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium mb-8"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="Growing Minds" className="h-14 w-auto" onError={(e)=>{e.currentTarget.style.display='none';}} />
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Set a new password</h1>
            <p className="text-slate-500">Choose something memorable</p>
          </div>
        </div>

        {done ? (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 text-center" data-testid="reset-password-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-bold text-slate-800 text-lg mb-2">Password reset</h2>
            <p className="text-slate-600 text-sm mb-4">
              Redirecting you to the login page...
            </p>
            <Link to="/login">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 font-semibold">
                Go to login
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">New password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-12 border-slate-200 pr-12"
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-slate-700 font-medium">Confirm new password</Label>
              <Input
                id="confirm"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your password"
                className="h-12 border-slate-200"
                required
                data-testid="reset-password-confirm"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-semibold"
              data-testid="reset-password-submit"
            >
              {loading ? 'Resetting...' : 'Reset password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
